import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { LocationService, Position } from '../../services/location.service';
import { MapsService, UnifiedPlace } from '../../services/maps.service';

interface OrderMethod {
  id: 'qr' | 'address' | 'gps';
  title: string;
  description: string;
  icon: string;
  available: boolean;
}

@Component({
  selector: 'app-order-request',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './order-request.component.html',
  styleUrl: './order-request.component.scss'
})
export class OrderRequestComponent implements OnInit {
  selectedMethod: string | null = null;
  addressForm: FormGroup;
  loading = false;
  currentPosition: GeolocationPosition | null = null;

  orderMethods: OrderMethod[] = [
    {
      id: 'qr',
      title: 'Escanear Código QR',
      description: 'Apunta la cámara al código QR del restaurante',
      icon: '📱',
      available: true
    },
    {
      id: 'address',
      title: 'Introducir Dirección',
      description: 'Escribe la dirección del restaurante manualmente',
      icon: '📍',
      available: true
    },
    {
      id: 'gps',
      title: 'Usar Mi Ubicación',
      description: 'Encuentra restaurantes cerca de ti',
      icon: '🗺️',
      available: true
    }
  ];

  nearbyRestaurants: any[] = [];

  constructor(
    private fb: FormBuilder,
    private locationService: LocationService,
    private mapsService: MapsService
  ) {
    this.addressForm = this.fb.group({
      address: ['', [Validators.required, Validators.minLength(5)]],
      city: ['', Validators.required],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]]
    });
  }

  ngOnInit(): void {
    this.checkGeolocationAvailability();
  }

  selectMethod(methodId: string): void {
    this.selectedMethod = methodId;
    
    if (methodId === 'gps') {
      this.getCurrentLocation();
    }
  }

  checkGeolocationAvailability(): void {
    if (!navigator.geolocation) {
      const gpsMethod = this.orderMethods.find(m => m.id === 'gps');
      if (gpsMethod) {
        gpsMethod.available = false;
        gpsMethod.description = 'GPS no disponible en tu dispositivo';
      }
    }
  }

  getCurrentLocation(): void {
    this.loading = true;
    
    this.locationService.getCurrentPosition().subscribe({
      next: (position: Position) => {
        this.currentPosition = {
          coords: {
            latitude: position.latitude,
            longitude: position.longitude,
            accuracy: position.accuracy
          },
          timestamp: position.timestamp
        } as GeolocationPosition;
        this.loading = false;
        this.loadNearbyRestaurants();
        console.log('Ubicación obtenida:', position);
      },
      error: (error) => {
        console.error('Error obteniendo ubicación:', error);
        this.loading = false;
        
        const gpsMethod = this.orderMethods.find(m => m.id === 'gps');
        if (gpsMethod) {
          gpsMethod.available = false;
          gpsMethod.description = 'No se pudo obtener la ubicación';
        }
      }
    });
  }

  loadNearbyRestaurants(): void {
    if (!this.currentPosition) return;

    this.loading = true;
    const lat = this.currentPosition.coords.latitude;
    const lng = this.currentPosition.coords.longitude;

    this.mapsService.searchRestaurants({
      lat,
      lng,
      radius: 1000, // 1km radius para pedidos
      limit: 10,
      useMapbox: true,
      useNominatim: true
    }).subscribe({
      next: (places: UnifiedPlace[]) => {
        this.nearbyRestaurants = places.map((place, index) => ({
          id: parseInt(place.id.replace(/\D/g, '')) || index + 1000,
          name: place.name,
          address: place.address,
          distance: place.distance ? `${(place.distance / 1000).toFixed(1)} km` : 'N/A',
          rating: 4.0 + Math.random() * 1.0,
          cuisineType: place.category || 'Restaurante',
          estimatedTime: `${Math.floor(Math.random() * 20) + 15}-${Math.floor(Math.random() * 10) + 25} min`
        }));
        this.loading = false;
      },
      error: (error) => {
        console.error('Error cargando restaurantes:', error);
        this.loading = false;
        // No fallback restaurants available
      }
    });
  }

  onQrScan(): void {
    // Navegar al escáner QR
    console.log('Navegando al escáner QR...');
  }

  onAddressSubmit(): void {
    if (this.addressForm.valid) {
      this.loading = true;
      const formData = this.addressForm.value;
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.postalCode}`;
      
      console.log('Buscando restaurantes en:', fullAddress);
      
      // Primero geocodificar la dirección
      this.mapsService.geocode(fullAddress, {
        country: 'es'
      }).subscribe({
        next: (results) => {
          if (results.length > 0) {
            const location = results[0];
            // Buscar restaurantes en esa ubicación
            this.mapsService.searchRestaurants({
              lat: location.lat,
              lng: location.lng,
              radius: 1000,
              limit: 10
            }).subscribe({
              next: (places: UnifiedPlace[]) => {
                this.nearbyRestaurants = places.map((place, index) => ({
                  id: parseInt(place.id.replace(/\D/g, '')) || index + 2000,
                  name: place.name,
                  address: place.address,
                  distance: place.distance ? `${(place.distance / 1000).toFixed(1)} km` : 'N/A',
                  rating: 4.0 + Math.random() * 1.0,
                  cuisineType: place.category || 'Restaurante',
                  estimatedTime: `${Math.floor(Math.random() * 20) + 15}-${Math.floor(Math.random() * 10) + 25} min`
                }));
                this.loading = false;
              },
              error: () => {
                this.loading = false;
              }
            });
          } else {
            this.loading = false;
            console.log('No se encontró la dirección');
          }
        },
        error: (error) => {
          console.error('Error geocodificando:', error);
          this.loading = false;
        }
      });
    }
  }

  onRestaurantSelect(restaurant: any): void {
    console.log('Restaurante seleccionado:', restaurant);
    // Implementar navegación al menú del restaurante
  }

  getStars(rating: number): string {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    return '⭐'.repeat(fullStars) + (hasHalfStar ? '✨' : '');
  }

  resetSelection(): void {
    this.selectedMethod = null;
    this.currentPosition = null;
    this.addressForm.reset();
  }
}
